import React, { useMemo } from 'react';
import {
    Box,
    Title,
    Tooltip,
    Group,
} from '@mantine/core';
import {
    IconSchool,
    IconBuilding,
    IconMapPin,
    IconBooks,
    IconApps,
} from '@tabler/icons-react';
import { getSingularLabel } from '../../utils/pluralizationUtils';
import { debugLog, debugVerboseLog } from '../../utils/debugLog';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import { scimBuildingService, scimClassService, scimResponsibilityCenterService, scimSchoolService } from '../../services/ScimLocalityService';
import type { Building, Class, ResponsibilityCenter, School } from '../../types/Localities';
import { LocalityEntityManagement } from './LocalityEntityManagement';
import { type TableColumn, DisplayNameCell } from '../common';
import DynamicEntityManagement from '../entities/DynamicEntityManagement';
import { entityService } from '../../services/entityServiceSingleton';

interface LocalityManagementProps {
    selectedLocalityType: string;
    isAuthenticated?: boolean;
    selectedEntityId?: string | null;
    selectedEntityViewMode?: 'view' | 'edit' | 'create' | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    /** When this changes (e.g. on-behalf-of toggled), tables refresh and per-entity entitlement is refetched. */
    onBehalfOfKey?: string;
    onUnsavedStateChange?: (state: { active: boolean; hasUnsavedChanges: boolean; mode: 'create' | 'edit'; entityType: string }) => void;
}

export const LocalityManagement: React.FC<LocalityManagementProps> = ({ selectedLocalityType, selectedEntityId, selectedEntityViewMode, deepLinkEntityType, authResolved = false, onBehalfOfKey, onUnsavedStateChange }) => {
    debugLog(`🔍 LocalityManagement: Received selectedLocalityType: "${selectedLocalityType}"`);

    const { isDisjointed } = usePerEntityTableEntitlement({
        entityType: selectedLocalityType?.toLowerCase(),
        authResolved,
        onBehalfOfKey,
        onlyWhenEntityTypeSet: false
    });

    // Address column for all locality entities (used when rendering via DynamicEntityManagement)
    const localityAddressColumn: TableColumn<any> = useMemo(() => ({
        key: 'address',
        label: 'Address',
        sortable: true,
        render: (entity: any) => entity.Address || entity.address || 'N/A'
    }), []);

    const renderContent = () => {
        debugLog(`🔍 LocalityManagement: renderContent called with selectedLocalityType: "${selectedLocalityType}"`);
        switch (selectedLocalityType) {
            case 'Building':
                debugLog('🔍 LocalityManagement: Rendering Building case');
                return (
                    <LocalityEntityManagement<Building>
                        entityType="Building"
                        hashBase="#localities/building"
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={deepLinkEntityType}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        onUnsavedStateChange={onUnsavedStateChange}
                        hideTableHeader={true}
                        search={(p) => scimBuildingService.searchBuildings(p)}
                        fetchById={(id) => entityService.getBuildingById(id)}
                        generateColumns={() => [
                            {
                                key: 'DisplayName',
                                label: 'Name',
                                title: 'DisplayName',
                                width: '300px',
                                sortable: true,
                                render: (building: Building, index: number) => (
                                    <DisplayNameCell
                                        displayName={building.DisplayName || (building as any).rawData?.DisplayName}
                                        id={(building as any).Id}
                                        index={index}
                                        startIndex={0}
                                    />
                                )
                            },
                            {
                                key: 'Address',
                                label: 'Address',
                                title: 'Address',
                                width: '300px',
                                sortable: true,
                                render: (building: Building) => (building as any).Address || 'N/A'
                            },
                            { key: 'Id', label: 'Id', title: 'Id', width: '100px', sortable: true }
                        ]}
                    />
                );
            case 'Class':
                debugLog('🔍 LocalityManagement: Rendering Class case');
                return (
                    <LocalityEntityManagement<Class>
                        entityType="Class"
                        hashBase="#classes"
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={deepLinkEntityType}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        onUnsavedStateChange={onUnsavedStateChange}
                        hideTableHeader={true}
                        disableRowSelectWhenEditing={true}
                        search={(p) => scimClassService.searchClasses(p)}
                        fetchById={(id) => entityService.getClassById(id)}
                        generateColumns={() => [
                            {
                                key: 'DisplayName',
                                label: 'Name',
                                title: 'DisplayName',
                                width: '40%',
                                sortable: true,
                                render: (classItem: Class, index: number) => (
                                    <DisplayNameCell
                                        displayName={(classItem as any).DisplayName || (classItem as any).rawData?.DisplayName}
                                        id={(classItem as any).Id}
                                        index={index}
                                        startIndex={0}
                                    />
                                )
                            },
                            { key: 'Type', label: 'Course', title: 'Course', width: '30%', sortable: true },
                            { key: 'Id', label: 'Id', title: 'Id', width: '30%', sortable: true }
                        ]}
                    />
                );
            case 'ResponsibilityCenter':
                debugVerboseLog('🔍 LocalityManagement: Rendering ResponsibilityCenter case');
                return (
                    <LocalityEntityManagement<ResponsibilityCenter>
                        entityType="ResponsibilityCenter"
                        hashBase="#localities/responsibilityCenter"
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={deepLinkEntityType}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        onUnsavedStateChange={onUnsavedStateChange}
                        hideTableHeader={true}
                        search={(p) => scimResponsibilityCenterService.searchResponsibilityCenters(p)}
                        fetchById={(id) => entityService.getResponsibilityCenterById(id)}
                        generateColumns={() => [
                            {
                                key: 'DisplayName',
                                label: 'Name',
                                title: 'DisplayName',
                                width: '300px',
                                sortable: true,
                                render: (rc: ResponsibilityCenter, index: number) => (
                                    <DisplayNameCell
                                        displayName={(rc as any).DisplayName || (rc as any).rawData?.DisplayName}
                                        id={(rc as any).Id}
                                        index={index}
                                        startIndex={0}
                                    />
                                )
                            },
                            {
                                key: 'Address',
                                label: 'Address',
                                title: 'Address',
                                width: '300px',
                                sortable: true,
                                render: (rc: ResponsibilityCenter) => (rc as any).Address || (rc as any).rawData?.Address || 'N/A'
                            },
                            { key: 'Id', label: 'Id', title: 'Id', width: '100px', sortable: true }
                        ]}
                    />
                );
            case 'School':
                debugLog('🔍 LocalityManagement: Rendering School case');
                return (
                    <LocalityEntityManagement<School>
                        entityType="School"
                        hashBase="#localities/school"
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={deepLinkEntityType}
                        authResolved={authResolved}
                        onBehalfOfKey={onBehalfOfKey}
                        onUnsavedStateChange={onUnsavedStateChange}
                        hideTableHeader={true}
                        search={(p) => scimSchoolService.searchSchools(p)}
                        fetchById={(id) => entityService.getSchoolById(id)}
                        generateColumns={() => [
                            {
                                key: 'DisplayName',
                                label: 'Name',
                                title: 'DisplayName',
                                width: '300px',
                                sortable: true,
                                render: (school: School, index: number) => (
                                    <DisplayNameCell
                                        displayName={(school as any).DisplayName || (school as any).rawData?.DisplayName}
                                        id={(school as any).Id}
                                        index={index}
                                        startIndex={0}
                                    />
                                )
                            },
                            {
                                key: 'Address',
                                label: 'Address',
                                title: 'Address',
                                width: '300px',
                                sortable: true,
                                render: (school: School) => (school as any).Address || (school as any).rawData?.Address || 'N/A'
                            },
                            { key: 'Id', label: 'Id', title: 'Id', width: '100px', sortable: true }
                        ]}
                    />
                );
            default:
                debugLog(`🔍 LocalityManagement: No matching case for "${selectedLocalityType}", using dynamic entity management`);
                return (
                    <DynamicEntityManagement
                        entityType={selectedLocalityType}
                        selectedEntityId={selectedEntityId}
                        selectedEntityViewMode={selectedEntityViewMode}
                        deepLinkEntityType={deepLinkEntityType}
                        authResolved={authResolved}
                        additionalColumns={[localityAddressColumn]}
                        onUnsavedStateChange={onUnsavedStateChange}
                    />
                );
        }
    };

    // Get icon component for locality type
    const getLocalityIcon = (localityType: string) => {
        switch (localityType.toLowerCase()) {
            case 'building':
                return <IconBuilding size={24} />;
            case 'school':
                return <IconSchool size={24} />;
            case 'class':
                return <IconBooks size={24} />;
            case 'responsibilitycenter':
                return <IconMapPin size={24} />;
            default:
                return <IconApps size={24} />;
        }
    };

    // Check if this locality type uses DynamicEntityManagement (which has its own header)
    const usesDynamicEntityManagement = !['Building', 'Class', 'ResponsibilityCenter', 'School'].includes(selectedLocalityType);

    return (
        <Box w="100%">
            {!usesDynamicEntityManagement && (
                <Group gap="xs" align="center" mb="sm">
                    {getLocalityIcon(selectedLocalityType)}
                    <Title order={4}>
                        {getSingularLabel(selectedLocalityType)} Management{isDisjointed ? (
                        <Tooltip label="Disjointed type">
                            <span> *</span>
                        </Tooltip>
                    ) : ''}
                    </Title>
                </Group>
            )}

            {renderContent()}
        </Box>
    );
};